import { EditInvoicePayload } from '../api/types';

export interface AllowListField {
  key: string;
  label: string;
  category: 'Identifiers' | 'Dates' | 'Vendor' | 'Customer' | 'Financials' | 'Payment' | 'Shipping';
  type: 'ref' | 'date' | 'amount' | 'address' | 'text';
}

export const ALLOW_LIST_FIELDS: AllowListField[] = [
  // Identifiers
  { key: 'InvoiceId', label: 'Invoice Number / ID', category: 'Identifiers', type: 'ref' },
  { key: 'PurchaseOrder', label: 'PO Number', category: 'Identifiers', type: 'ref' },
  { key: 'CustomerId', label: 'Customer ID', category: 'Identifiers', type: 'ref' },

  // Dates
  { key: 'InvoiceDate', label: 'Invoice Date', category: 'Dates', type: 'date' },
  { key: 'DueDate', label: 'Due Date', category: 'Dates', type: 'date' },
  { key: 'ServiceStartDate', label: 'Service Start Date', category: 'Dates', type: 'date' },
  { key: 'ServiceEndDate', label: 'Service End Date', category: 'Dates', type: 'date' },

  // Vendor
  { key: 'VendorName', label: 'Vendor Name', category: 'Vendor', type: 'text' },
  { key: 'VendorTaxId', label: 'Vendor VAT / Tax ID', category: 'Vendor', type: 'ref' },
  { key: 'VendorAddress', label: 'Vendor Address', category: 'Vendor', type: 'address' },
  { key: 'VendorAddressRecipient', label: 'Vendor Address Recipient', category: 'Vendor', type: 'address' },
  { key: 'VendorPhoneNumber', label: 'Vendor Phone Number', category: 'Vendor', type: 'text' },
  { key: 'BillingEmail', label: 'Billing / Vendor Email', category: 'Vendor', type: 'text' },

  // Customer
  { key: 'CustomerName', label: 'Customer Name', category: 'Customer', type: 'text' },
  { key: 'CustomerTaxId', label: 'Customer VAT / Tax ID', category: 'Customer', type: 'ref' },
  { key: 'CustomerAddress', label: 'Customer Address', category: 'Customer', type: 'address' },
  { key: 'CustomerAddressRecipient', label: 'Customer Address Recipient', category: 'Customer', type: 'address' },

  // Financials
  { key: 'SubTotal', label: 'Subtotal (Net)', category: 'Financials', type: 'amount' },
  { key: 'TotalTax', label: 'Total Tax / VAT', category: 'Financials', type: 'amount' },
  { key: 'InvoiceTotal', label: 'Invoice Grand Total', category: 'Financials', type: 'amount' },
  { key: 'AmountDue', label: 'Amount Due', category: 'Financials', type: 'amount' },
  { key: 'PreviousUnpaidBalance', label: 'Previous Unpaid Balance', category: 'Financials', type: 'amount' },

  // Payment
  { key: 'PaymentTerm', label: 'Payment Terms', category: 'Payment', type: 'text' },
  { key: 'PaymentDetails', label: 'Payment Details / Notes', category: 'Payment', type: 'text' },
  { key: 'RemittanceAddress', label: 'Remittance Address', category: 'Payment', type: 'address' },

  // Shipping
  { key: 'BillingAddress', label: 'Billing Address', category: 'Shipping', type: 'address' },
  { key: 'ShippingAddress', label: 'Shipping Address', category: 'Shipping', type: 'address' },
];

export function getFieldType(key: string): 'ref' | 'date' | 'amount' | 'address' | 'text' {
  const matched = ALLOW_LIST_FIELDS.find((f) => f.key.toLowerCase() === key.toLowerCase());
  if (matched) return matched.type;
  if (/date/i.test(key)) return 'date';
  if (/total|amount|subtotal|tax|balance|price/i.test(key)) return 'amount';
  if (/id|number|po|taxid|vat|ref/i.test(key)) return 'ref';
  if (/address|street|city|zip|country/i.test(key)) return 'address';
  return 'text';
}

export interface HeaderFieldForm {
  id: string;
  key: string;
  value: string;
  type: 'ref' | 'text' | 'date' | 'amount' | 'address';
  isNew: boolean;
}

export interface LineItemForm {
  id: string;
  originalIndex?: number; // Present if loaded from server, undefined if new
  description: string;
  quantity: string;
  unitPrice: string;
  amount: string;
  isDeleted?: boolean;
}

/**
 * Builds the exact PATCH payload required by API v2
 * Strictly separating existing fields from add_fields and tracking original line item indices
 */
export function buildPatchPayload(
  initialHeaderFields: Record<string, string>,
  currentHeaderFields: HeaderFieldForm[],
  lineItems: LineItemForm[],
  initialLineItems: LineItemForm[] = []
): EditInvoicePayload {
  const payload: EditInvoicePayload = {};

  // 1. Existing header fields modified
  const modifiedFields: Record<string, string> = {};
  currentHeaderFields
    .filter((f) => !f.isNew && f.key)
    .forEach((f) => {
      const initialVal = initialHeaderFields[f.key] ?? '';
      if (f.value !== initialVal) {
        modifiedFields[f.key] = f.value;
      }
    });
  if (Object.keys(modifiedFields).length > 0) {
    payload.fields = modifiedFields;
  }

  // 2. New header fields added
  const addedFields: Record<string, string> = {};
  currentHeaderFields
    .filter((f) => f.isNew && f.key && f.value)
    .forEach((f) => {
      addedFields[f.key] = f.value;
    });
  if (Object.keys(addedFields).length > 0) {
    payload.add_fields = addedFields;
  }

  // 3. Existing line items modified (by 0-based original index)
  const modifiedItems: Array<{ index: number; values: Record<string, string> }> = [];
  lineItems
    .filter((item) => item.originalIndex !== undefined && !item.isDeleted)
    .forEach((item) => {
      if (initialLineItems && initialLineItems.length > 0) {
        const initItem = initialLineItems.find((init) => init.originalIndex === item.originalIndex);
        const isChanged =
          !initItem ||
          initItem.description !== item.description ||
          initItem.quantity !== item.quantity ||
          initItem.unitPrice !== item.unitPrice ||
          initItem.amount !== item.amount;

        if (!isChanged) return;
      }

      modifiedItems.push({
        index: item.originalIndex!,
        values: {
          Description: item.description.trim() || 'Line Item',
          Quantity: item.quantity || '1',
          UnitPrice: item.unitPrice || item.amount || '0.00',
          Amount: item.amount || '0.00',
        },
      });
    });
  if (modifiedItems.length > 0) {
    payload.items = modifiedItems;
  }

  // 4. Existing line items deleted (0-based original index)
  const deletedIndices = lineItems
    .filter((item) => item.originalIndex !== undefined && item.isDeleted)
    .map((item) => item.originalIndex!);
  if (deletedIndices.length > 0) {
    payload.remove_items = deletedIndices;
  }

  // 5. Brand new line items added (Description is mandatory)
  const newItems = lineItems
    .filter((item) => item.originalIndex === undefined && !item.isDeleted && item.description.trim())
    .map((item) => ({
      Description: item.description.trim(),
      Quantity: item.quantity || '1',
      UnitPrice: item.unitPrice || item.amount || '0.00',
      Amount: item.amount || '0.00',
    }));
  if (newItems.length > 0) {
    payload.add_items = newItems;
  }

  return payload;
}
